<template>
  <div class="ml-3">
    <v-card class="pa-0">
      <v-card-title>{{ $t("Reports") }}</v-card-title>
      <v-card-text>
        <v-select
          v-bind:label="$t('Select')"
          v-model="selectedReport"
          :items="reportList"
          item-text="text"
          item-value="id"
          return-object
        ></v-select>
        <v-row v-if="selectedReport.id == 1">
          <v-text-field
            readonly
            v-model="documentReportFilter.displayConditionStr"
            :label="$t('Condition')"
          ></v-text-field>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" color="primary" small @click="editCondition">
                <v-icon>{{
                  documentReportFilter.conditionStr == ""
                    ? $t("mdi-plus")
                    : $t("mdi-pencil")
                }}</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("AddCondition") }}</span>
          </v-tooltip>
        </v-row>
        <v-row v-if="selectedReport.id == 2">
          <v-radio-group v-model="securityReportFilter.groupBy" row>
            <v-radio v-bind:label="$t('User')" value="1"></v-radio>
            <v-radio v-bind:label="$t('ContentType')" value="2"></v-radio>
          </v-radio-group>
        </v-row>
        <v-row v-if="selectedReport.id == 3">
          <v-col>
            <v-select
              v-bind:label="$t('Group')"
              v-model="auditReportFilter.auditGroup"
              :items="groupList"
              @change="filterTypesByGroups"
              item-text="text"
              item-value="id"
              clearable
            ></v-select>
          </v-col>
          <v-col>
            <BaseDatePicker
              v-model="auditReportFilter.dateFrom"
              clearable
              :label="$t('From')"
            />
          </v-col>
          <v-col>
            <BaseDatePicker
              v-model="auditReportFilter.dateTo"
              clearable
              :label="$t('To')"
            />
          </v-col>
          <v-col>
            <v-autocomplete
              v-bind:label="$t('User')"
              v-model="auditReportFilter.usersId"
              :items="userList"
              item-text="text"
              item-value="id"
              multiple
              clearable
            >
              <template v-slot:selection="{ item, index }">
                <span>{{ getUserSelection(index, item) }}</span>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col>
            <v-autocomplete
              v-bind:label="$t('Type')"
              v-model="auditReportFilter.auditTypes"
              :items="typeList"
              item-text="text"
              item-value="id"
              multiple
              clearable
            >
              <template v-slot:selection="{ item, index }">
                <span>{{ getAuditTypeSelection(index, item) }}</span>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-btn color="primary" @click="showReport">
          {{ $t("GenerateReport") }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-container fluid>
      <ReportModal ref="reportModal" :load="false" v-if="!loading" />
    </v-container>
    <SearchCriteriaBuilderModal
      :ref="conditionModalKey"
      :contentTypeIDs="userContentTypes"
      @on-condition-saved="onConditionChanged"
    />
  </div>
</template>

<script>
import { required, maxLength, email } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import ReportModal from "@/components/report/ReportModal";
import AuditService from "@/services/AuditService";
import UserService from "@/services/UserService";
import Utils from "@/ecm/common/Utils";
import SearchCriteriaBuilderModal from "@/components/search/SearchCriteriaBuilderModal.vue";
import SearchService from "@/services/SearchService";
import AuditType from "@/ecm/variables/AuditType.js";

export default {
  components: {
    SearchCriteriaBuilderModal,
    ReportModal,
  },
  data() {
    return {
      reportList: [
        { id: 1, text: this.$t("DocumentsStatisticsReport") },
        { id: 2, text: this.$t("SecurityReport") },
        { id: 3, text: this.$t("Audits") },
      ],
      selectedReport: { id: 1, text: this.$t("DocumentsStatisticsReport") },
      groupList: [
        { id: 0, text: this.$t("All") },
        { id: 1, text: this.$t("Administration") },
        { id: 2, text: this.$t("Documents") },
        { id: 3, text: this.$t("Searches") },
        { id: 4, text: this.$t("Flows") },
        { id: 5, text: this.$t("Delegations") },
      ],
      userList: [],
      typeList: [],
      auditReportFilter: {
        auditGroup: 0,
        dateFrom: null,
        dateTo: null,
        usersId: [],
        auditTypes: [],
      },
      securityReportFilter: {
        groupBy: "1",
      },
      documentReportFilter: {
        conditionStr: "",
        displayConditionStr: "",
      },
      loading: false,
      conditionModalKey: "conditionModalKey",
    };
  },
  mixins: [validationMixin],
  validations: {
    modelEntity: {
      description: { required, maxLength: maxLength(50) },
      type: { required },
      delegate: { required },
    },
  },
  created() {
    AuditService.getTypes().then((data) => {
      this.typeList = data;
    });
    UserService.getList().then((data) => {
      this.userList = data;
    });

    UserService.checkRights().then({});
  },
  computed: {
    conditionModalInstance() {
      return this.$refs[this.conditionModalKey];
    },
    userContentTypes() {
      return this.$store.getters["account/userContentTypes"].map((x) => x.id);
    },
    descriptionErrors() {
      const errors = [];
      if (!this.$v.modelEntity.description.$dirty) return errors;
      !this.$v.modelEntity.description.maxLength &&
        errors.push(
          "La descripcion debe tener un tamaño máximo de 100 caracteres"
        );
      return errors;
    },
  },
  methods: {
    getAuditTypeSelection(index, item) {
      if (index === 0) {
        if (this.auditReportFilter.auditTypes.length === 1) return item.text;

        if (
          this.auditReportFilter.auditTypes.length > 1 &&
          this.auditReportFilter.auditTypes.length != this.typeList.length
        )
          return `${this.auditReportFilter.auditTypes.length} ${this.$t(
            "Selected"
          )}`;

        if (this.auditReportFilter.auditTypes.length === this.typeList.length)
          return this.$t("All");
      }
    },
    getUserSelection(index, item) {
      if (index === 0) {
        if (this.auditReportFilter.usersId.length === 1) return item.text;

        if (
          this.auditReportFilter.usersId.length > 1 &&
          this.auditReportFilter.usersId.length != this.userList.length
        )
          return `${this.auditReportFilter.usersId.length} ${this.$t(
            "Selected"
          )}`;

        if (this.auditReportFilter.usersId.length === this.userList.length)
          return this.$t("All");
      }
    },
    showReport() {
      this.loading = true;
      var reportParam = {};

      if (this.selectedReport.id == 1) {
        if (!this.documentReportFilter.objectId) {
          Utils.toast.error(this.$t("ConditionRequired"));
          return;
        }
        reportParam.objectId = this.documentReportFilter.objectId;
        reportParam.reportName = "DocumentCount.trdp";
      }

      if (this.selectedReport.id == 2) {
        if (this.securityReportFilter.groupBy == "1")
          reportParam.reportName = "SecurityByUser.trdp";
        else reportParam.reportName = "SecurityByContentType.trdp";
      }

      if (this.selectedReport.id == 3) {
        reportParam.auditGroup = this.auditReportFilter.auditGroup;
        reportParam.dateFrom = "";
        if (this.auditReportFilter.dateFrom) {
          reportParam.dateFrom = Utils.date.changeFormat(
            this.auditReportFilter.dateFrom,
            this.$store.getters["account/user"].MetricDateFormat,
            "DD/MM/YYYY"
          );
        }
        reportParam.dateTo = "";
        if (this.auditReportFilter.dateTo) {
          reportParam.dateTo = Utils.date.changeFormat(
            this.auditReportFilter.dateTo,
            this.$store.getters["account/user"].MetricDateFormat,
            "DD/MM/YYYY"
          );
        }
        reportParam.usersId =
          this.auditReportFilter.usersId.length > 0
            ? this.auditReportFilter.usersId.join()
            : "0";
        reportParam.auditTypes =
          this.auditReportFilter.auditTypes.length > 0
            ? this.auditReportFilter.auditTypes.join()
            : "0";
        reportParam.reportName = "AuditReport.trdp";
      }

      this.loading = false;
      this.$refs.reportModal.show(this.selectedReport.text, reportParam);
    },
    editCondition() {
      this.conditionModalInstance.edit(this.documentReportFilter.conditionStr);
    },
    onConditionChanged(condition) {
      this.documentReportFilter.conditionStr = condition.conditionStr;
      this.documentReportFilter.displayConditionStr =
        condition.displayConditionStr;
      if (condition.conditionStr) {
        SearchService.getDocumentsByCriteria({
          SearchPattern: condition.conditionStr,
        }).then((data) => {
          this.documentReportFilter.objectId = data;
        });
      }
    },
    filterTypesByGroups() {
      if (this.selectedReport.id == 3) {
        this.auditReportFilter.auditTypes = [];
        AuditService.getTypes().then((data) => {
          this.typeList = data;
          let typesInGroup = this.getTypesOfGroup();

          if (typesInGroup.length != 0) {
            let newTypes = [];
            for (let l = 0; l < this.typeList.length; l++) {
              let id = parseInt(this.typeList[l].id);
              if (typesInGroup.includes(id)) {
                newTypes.push(this.typeList[l]);
              }
            }
            this.typeList = newTypes;
          }
        });
      }
    },
    getTypesOfGroup() {
      switch (this.auditReportFilter.auditGroup) {
        case this.groupList[1].id:
          return [
            AuditType.ADMINISTRATION_DELETE,
            AuditType.ADMINISTRATION_UPDATE,
            AuditType.ADMINISTRATION_CREATE,
          ];
        case this.groupList[2].id:
          return [
            AuditType.DOCUMENT_DELETE,
            AuditType.DOCUMENT_INSERT,
            AuditType.DOCUMENT_UPDATE,
            AuditType.DOCUMENT_UPDATE_ATTACHMENT,
            AuditType.DOCUMENT_DELETE_PERMANENTLY,
            AuditType.DOCUMENT_RESTORE,
            AuditType.DOCUMENT_ARCHIVE,
            AuditType.DOCUMENT_DOWNLOAD,
            AuditType.DOCUMENT_DISPLAY,
            AuditType.DOCUMENT_PRINT,
            AuditType.DOCUMENT_AUTO_UPDATE,
            AuditType.DOCUMENT_CONTENT_TYPE_CHANGE,
            AuditType.DOCUMENT_SIGN,
            AuditType.DOCUMENT_SEND_BY_EMAIL,
            AuditType.COMMENT_CREATE,
            AuditType.COMMENT_CREATE_EXTERNAL,
            AuditType.COMMENT_UPDATE,
            AuditType.COMMENT_DELETE,
            AuditType.DOCUMENT_ATTACHMENT_INSERT,
            AuditType.DOCUMENT_ATTACHMENT_UPDATE,
            AuditType.DOCUMENT_ATTACHMENT_DELETE,
            AuditType.DOCUMENT_ATTACHMENT_DISPLAY,
            AuditType.DOCUMENT_ATTACHMENT_DOWNLOAD,
            AuditType.DOCUMENT_DISPLAY_EXTERNAL,
            AuditType.DOCUMENT_SIGN_EXTERNAL,
            AuditType.DOCUMENT_DOWNLOAD_EXTERNAL,
            AuditType.DOCUMENT_VALIDATE_EXTERNAL,
            AuditType.DOCUMENT_REJECT_EXTERNAL,
          ];
        case this.groupList[3].id:
          return [
            AuditType.USER_ADVANCED_SEARCH,
            AuditType.USER_NATURAL_SEARCH,
            AuditType.USER_BASKET_SEARCH,
            AuditType.USER_NOTIFICATION_SEARCH,
          ];
        case this.groupList[4].id:
          return [
            AuditType.FLOW_VALIDATE_ACTION,
            AuditType.FLOW_REFUSE_ACTION,
            AuditType.FLOW_DISTRIBUTE_ACTION,
          ];
        case this.groupList[5].id:
          return [
            AuditType.DELEGATION_SCHEDULED_START,
            AuditType.DELEGATION_SCHEDULED_END,
            AuditType.DELEGATION_MANUAL_START,
            AuditType.DELEGATION_MANUAL_END,
          ];
        default:
          return [];
      }
    },
  },
};
</script>
